// =============================================================
// 📁 src/pages/planification-logistique/planificateur/FicheView.tsx
// 💎 Version Platinum PRO 2025 — Modulaire & Stable
// =============================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../../../lib/axios";
import Badge from "../../../components/ui/Badge";
import MissionInfoCard from "../../../components/planificateur/MissionInfoCard";
import MissionAffectationCard from "../../../components/planificateur/MissionAffectationCard";
import MissionExecutionCard from "../../../components/planificateur/MissionExecutionCard";
import MissionPointsCard from "../../../components/planificateur/MissionPointsCard";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";

/* =============================================================
   📦 Typage robuste
============================================================= */
interface Mission {
  id: number;
  date?: string;

  quartier?: string;
  zone?: string;
  lieu?: string;

  equipe?: string;
  agent?: string;
  vehicule?: string;

  statut: string;
  commentaire?: string;

  historique?: { action: string; date: string }[];
}

/* =============================================================
   ⏳ Loader
============================================================= */
const Loader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900 text-gray-400">
    ⏳ Chargement de la fiche de mission...
  </div>
);

/* =============================================================
   🧩 Composant principal
============================================================= */
const FicheView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [mission, setMission] = useState<Mission | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchMission = async () => {
      try {
        const res = await api.get(`/planificateur/${id}`);
        const raw = res.data?.descente || res.data;

const mapped: Mission = {
  id: raw.id,
  date: raw.date,

  quartier:
    raw.quartier?.nom ||
    raw.quartier?.libelle ||
    raw.quartier ||
    raw.zone?.nom ||
    "—",

  zone:
    raw.zone?.nom ||
    raw.zone?.libelle ||
    raw.zone ||
    "—",

  lieu: raw.lieu || "—",

  equipe:
    raw.equipe?.nom ||
    raw.equipe?.libelle ||
    raw.equipe ||
    "—",

  agent:
    raw.agent?.nom ||
    raw.agent?.prenom
      ? `${raw.agent?.prenom ?? ""} ${raw.agent?.nom ?? ""}`.trim()
      : raw.chauffeur?.nom ||
        raw.chauffeur?.prenom
      ? `${raw.chauffeur?.prenom ?? ""} ${raw.chauffeur?.nom ?? ""}`.trim()
      : typeof raw.agent === "string"
      ? raw.agent
      : "—",

  vehicule:
    raw.vehicule?.immatriculation ||
    raw.vehicule?.nom ||
    raw.vehicule ||
    "—",

  statut: raw.statut || "INCONNU",
  commentaire: raw.commentaire,
  historique: Array.isArray(raw.historique)
    ? raw.historique
    : [],
};

        setMission(mapped);
        setErreur(null);
      } catch (err) {
        console.error("Erreur chargement fiche :", err);
        setErreur("❌ Impossible de charger la mission.");
      } finally {
        setLoading(false);
      }
    };

    fetchMission();
  }, [id]);

  if (loading) return <Loader />;
  if (erreur)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-red-400 text-lg">
        {erreur}
      </div>
    );
  if (!mission)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-red-400 text-lg">
        ❌ Mission introuvable
      </div>
    );

const statutNormalized = mission.statut?.toUpperCase() ?? "INCONNU";

const handleCloturer = async () => {
  try {
    await api.put(`/descentes/${mission.id}/cloturer`);

    setMission((prev) =>
      prev ? { ...prev, statut: "TERMINEE" } : prev
    );

    toast.success("Mission clôturée avec succès");

  } catch (err) {
    console.error("Erreur clôture :", err);
    toast.error("Erreur lors de la clôture");
  }
};

const badgeVariant =
  statutNormalized === "TERMINEE"
    ? "success"
    : statutNormalized === "EN_COURS"
    ? "warning"
    : statutNormalized === "ANNULEE"
    ? "error"
    : "default";

const badgeClasses =
  badgeVariant === "success"
    ? "bg-green-600/20 text-green-400 border-green-600/40"
    : badgeVariant === "warning"
    ? "bg-yellow-600/20 text-yellow-400 border-yellow-600/40"
    : badgeVariant === "error"
    ? "bg-red-600/20 text-red-400 border-red-600/40"
    : "bg-slate-700/30 text-gray-400 border-slate-600";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-gray-100 p-6 flex justify-center">
      <ToastContainer theme="dark" />

      <motion.div
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-4xl space-y-6"
      >
        {/* 🧭 Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            🧾 Fiche Mission #{mission.id}
          </h1>

          <button
            onClick={() => navigate(-1)}
            className="bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg transition"
          >
            ◀ Retour
          </button>
        </div>

        {/* 📌 Statut global */}
        <div className="flex items-center gap-2">
          <span>Statut :</span>
          <Badge variant={badgeVariant}>
            {mission.statut}
          </Badge>
        </div>

        {/* 🧾 Info */}
        <MissionInfoCard
          missionId={mission.id}
          date={mission.date}
          zone={mission.zone}
          quartier={mission.quartier}
          lieu={mission.lieu}
        />

        {/* 🚛 Affectation */}
        <MissionAffectationCard
          equipe={mission.equipe}
          chefEquipe={mission.agent}
          vehicule={mission.vehicule}
        />

        {/* 🚀 Execution */}
        <MissionExecutionCard
          statut={mission.statut}
          dateDebut={mission.date}
        />

        {/* 📍 Points */}
        <MissionPointsCard missionId={mission.id} />

        {/* 📝 Commentaire */}
        {mission.commentaire && (
          <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700">
            <strong className="block mb-2">
              📝 Commentaire :
            </strong>
            <p className="whitespace-pre-line">
              {mission.commentaire}
            </p>
          </div>
        )}

        {/* 📊 Historique */}
        {mission.historique &&
          mission.historique.length > 0 && (
            <div className="bg-slate-900/40 p-4 rounded-lg border border-slate-700">
              <strong className="block mb-3">
                📊 Historique :
              </strong>

              <ul className="divide-y divide-slate-700">
                {mission.historique.map((h, i) => (
                  <li
                    key={i}
                    className="py-2 flex justify-between text-sm"
                  >
                    <span>{h.action}</span>
                    <span className="text-gray-400">
                      {new Date(h.date).toLocaleString(
                        "fr-FR"
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

<div className="flex justify-end gap-2 pt-4 border-t border-slate-700">

  {mission.statut !== "TERMINEE" && (
    <button
      onClick={handleCloturer}
      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
    >
      ✅ Clôturer
    </button>
  )}

  <button
    onClick={() => navigate("/admin/planificateur")}
    className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition"
  >
    🗂 Retour liste
  </button>

</div>

        <div className="text-center text-gray-500 text-xs pt-4">
          © {new Date().getFullYear()} EspoirAfriqueSas
        </div>
      </motion.div>
    </div>
  );
};
 
export default FicheView;